import { useEffect, useRef, useCallback } from 'react';
import { RetrofitSurveyData } from '../utils/retrofitSurveyStorage';
import { useRetrofitPhotoUpload } from './useRetrofitPhotoUpload';

interface MigrationStats {
  totalPhotos: number;
  photosNeedingMigration: number;
  photosMigrated: number;
  photosWithCDN: number;
  errors: number;
}

interface PhotoToMigrate {
  section: string;
  photoType: string;
  displayId: string;
  dataUrl: string;
  format: string;
}

/**
 * Centralized photo migration hook that runs once on survey load
 * Scans all sections and migrates base64 photos to CDN URLs automatically
 */
export const useCentralizedPhotoMigration = (
  surveyData: RetrofitSurveyData | undefined,
  surveyId: string | undefined,
  onDataUpdate: ((updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void) | undefined,
  isOnline: boolean = navigator.onLine
) => {
  const { queuePhotoUpload } = useRetrofitPhotoUpload(surveyId);
  const migrationRunRef = useRef(false);
  const lastSurveyIdRef = useRef<string>();
  const statsRef = useRef<MigrationStats>({
    totalPhotos: 0,
    photosNeedingMigration: 0,
    photosMigrated: 0,
    photosWithCDN: 0,
    errors: 0
  });

  const scanAndMigratePhotos = useCallback(() => {
    if (!surveyData || !surveyId || !onDataUpdate || !queuePhotoUpload || !isOnline) {
      console.log('[CentralizedMigration] Skipping migration:', {
        hasSurveyData: !!surveyData,
        hasSurveyId: !!surveyId,
        hasOnDataUpdate: !!onDataUpdate,
        hasQueuePhotoUpload: !!queuePhotoUpload,
        isOnline
      });
      return;
    }

    if (migrationRunRef.current) {
      console.log('[CentralizedMigration] ⏭️ Migration already executed');
      return;
    }

    console.log('[CentralizedMigration] 🔍 Starting centralized photo migration scan...');
    migrationRunRef.current = true;

    const photosToMigrate: PhotoToMigrate[] = [];
    const stats: MigrationStats = {
      totalPhotos: 0,
      photosNeedingMigration: 0,
      photosMigrated: 0,
      photosWithCDN: 0,
      errors: 0
    };

    // Helper to check if photo needs migration
    const needsMigration = (photo: any): boolean => {
      if (!photo) return false;
      return (photo.dataUrl || photo.base64String) && (!photo.url || photo.url === '');
    };

    // Helper to extract displayId
    const getDisplayId = (photo: any): string => {
      return photo.displayId || photo.id || `photo_${Date.now()}_${Math.random()}`;
    };

    // Helper to get dataUrl
    const getDataUrl = (photo: any): string => {
      return photo.dataUrl || (photo.base64String ? `data:image/${photo.format || 'jpeg'};base64,${photo.base64String}` : '');
    };

    // 1. Scan initial_questionnaire photos
    if (surveyData.initial_questionnaire?.photos) {
      Object.entries(surveyData.initial_questionnaire.photos).forEach(([sectionName, section]: [string, any]) => {
        if (section?.photos && Array.isArray(section.photos)) {
          section.photos.forEach((photo: any) => {
            stats.totalPhotos++;
            if (needsMigration(photo)) {
              stats.photosNeedingMigration++;
              photosToMigrate.push({
                section: 'initial_questionnaire',
                photoType: sectionName,
                displayId: getDisplayId(photo),
                dataUrl: getDataUrl(photo),
                format: photo.format || 'jpeg'
              });
            } else if (photo.url && photo.url.startsWith('http')) {
              stats.photosWithCDN++;
            }
          });
        }
      });
    }

    // 2. Scan property elevation photos (property_front, property_rear, property_left, property_right)
    const scanPropertySection = (sectionName: string, sectionData: any) => {
      if (!sectionData) return;

      const photoFields = [
        'detachmentType',
        'orientationImage',
        'frontElevationImage',
        'rearElevationImage',
        'leftElevationImage',
        'rightElevationImage',
        'roofTypeEvidenceImages',
        'gutteringDrainageImages',
        'wallThicknessImages',
        'wallThicknessFromDoor',
        'wallThicknessFromWindow',
        'wallDefectImages',
        'dpcEvidenceImages',
        'floorConstructionEvidenceImages',
        'rearBackyardImages',
        'rightSideImages',
        'leftSideImages',
        'wallTypeEvidenceImage'
      ];

      photoFields.forEach(fieldName => {
        const field = sectionData[fieldName];
        if (!field) return;

        // Handle array fields
        if (Array.isArray(field)) {
          field.forEach((photo: any) => {
            stats.totalPhotos++;
            if (needsMigration(photo)) {
              stats.photosNeedingMigration++;
              photosToMigrate.push({
                section: sectionName,
                photoType: fieldName,
                displayId: getDisplayId(photo),
                dataUrl: getDataUrl(photo),
                format: photo.format || 'jpeg'
              });
            } else if (photo.url && photo.url.startsWith('http')) {
              stats.photosWithCDN++;
            }
          });
        } else if (typeof field === 'object') {
          // Handle single photo object
          stats.totalPhotos++;
          if (needsMigration(field)) {
            stats.photosNeedingMigration++;
            photosToMigrate.push({
              section: sectionName,
              photoType: fieldName,
              displayId: getDisplayId(field),
              dataUrl: getDataUrl(field),
              format: field.format || 'jpeg'
            });
          } else if (field.url && field.url.startsWith('http')) {
            stats.photosWithCDN++;
          }
        }
      });
    };

    scanPropertySection('property_front', surveyData.property_front);
    scanPropertySection('property_rear', surveyData.property_rear);
    scanPropertySection('property_left', surveyData.property_left);
    scanPropertySection('property_right', surveyData.property_right);

    // 3. Scan heating_system photos
    if (surveyData.heating_system?.photos) {
      Object.entries(surveyData.heating_system.photos).forEach(([photoType, photoData]: [string, any]) => {
        if (photoData && typeof photoData === 'object') {
          stats.totalPhotos++;
          if (needsMigration(photoData)) {
            stats.photosNeedingMigration++;
            photosToMigrate.push({
              section: 'heating_system',
              photoType,
              displayId: getDisplayId(photoData),
              dataUrl: getDataUrl(photoData),
              format: photoData.format || 'jpeg'
            });
          } else if (photoData.url && photoData.url.startsWith('http')) {
            stats.photosWithCDN++;
          }
        }
      });
    }

    // 4. Scan room photos (stored as URLs in string arrays)
    if (surveyData.room_details?.rooms) {
      surveyData.room_details.rooms.forEach(room => {
        if (Array.isArray(room.photos)) {
          room.photos.forEach(photoUrl => {
            if (photoUrl) {
              stats.totalPhotos++;
              if (typeof photoUrl === 'string' && photoUrl.startsWith('data:image')) {
                stats.photosNeedingMigration++;
                // Room photos are stored as data URLs directly
                console.log(`[CentralizedMigration] ⚠️ Room ${room.name} has base64 photo - needs manual migration`);
              } else if (typeof photoUrl === 'string' && photoUrl.startsWith('http')) {
                stats.photosWithCDN++;
              }
            }
          });
        }
      });
    }

    // 5. Scan measure_specification elevations (wall insulation and RIR elevations)
    const scanElevations = (elevations: any[] | undefined, elevationType: string) => {
      if (!elevations || !Array.isArray(elevations)) return;
      
      elevations.forEach(elevation => {
        if (elevation.dataUrl || elevation.base64String) {
          stats.totalPhotos++;
          if (elevation.dataUrl || elevation.base64String) {
            stats.photosNeedingMigration++;
            console.log(`[CentralizedMigration] ⚠️ ${elevationType} elevation has base64 data - needs migration`);
            // Note: Elevations are handled differently, logged for awareness
          }
        }
      });
    };
    
    if (surveyData.measure_specification) {
      scanElevations(surveyData.measure_specification.wallInsulationElevations, 'Wall Insulation');
      scanElevations(surveyData.measure_specification.rirExteriorElevations, 'RIR Exterior');
    }

    // 6. Scan extensions photos
    if (surveyData.extensions) {
      const scanExtensionPhotos = (extensions: any[] | undefined) => {
        if (!extensions || !Array.isArray(extensions)) return;
        
        extensions.forEach(ext => {
          if (ext.photos && Array.isArray(ext.photos)) {
            ext.photos.forEach((photo: any) => {
              stats.totalPhotos++;
              if (needsMigration(photo)) {
                stats.photosNeedingMigration++;
                photosToMigrate.push({
                  section: 'extensions',
                  photoType: `extension_${ext.id || ext.type}`,
                  displayId: getDisplayId(photo),
                  dataUrl: getDataUrl(photo),
                  format: photo.format || 'jpeg'
                });
              } else if (photo.url && photo.url.startsWith('http')) {
                stats.photosWithCDN++;
              }
            });
          }
        });
      };
      
      scanExtensionPhotos(surveyData.extensions.existingExtensions);
      scanExtensionPhotos(surveyData.extensions.proposedExtensions);
    }

    // 7. Scan ventilation_condition photos (if they exist)
    const ventilationCondition = surveyData.ventilation_condition as any;
    if (ventilationCondition?.photos) {
      Object.entries(ventilationCondition.photos).forEach(([photoType, photoData]: [string, any]) => {
        if (photoData) {
          if (Array.isArray(photoData)) {
            photoData.forEach((photo: any) => {
              stats.totalPhotos++;
              if (needsMigration(photo)) {
                stats.photosNeedingMigration++;
                photosToMigrate.push({
                  section: 'ventilation_condition',
                  photoType,
                  displayId: getDisplayId(photo),
                  dataUrl: getDataUrl(photo),
                  format: photo.format || 'jpeg'
                });
              } else if (photo.url && photo.url.startsWith('http')) {
                stats.photosWithCDN++;
              }
            });
          } else if (typeof photoData === 'object' && needsMigration(photoData)) {
            stats.totalPhotos++;
            stats.photosNeedingMigration++;
            photosToMigrate.push({
              section: 'ventilation_condition',
              photoType,
              displayId: getDisplayId(photoData),
              dataUrl: getDataUrl(photoData),
              format: photoData.format || 'jpeg'
            });
          } else if (photoData.url && photoData.url.startsWith('http')) {
            stats.photosWithCDN++;
          }
        }
      });
    }

    // 8. Scan site_assessment photos (if they exist)
    const siteAssessment = surveyData.site_assessment as any;
    if (siteAssessment?.photos) {
      Object.entries(siteAssessment.photos).forEach(([photoType, photoData]: [string, any]) => {
        if (photoData) {
          if (Array.isArray(photoData)) {
            photoData.forEach((photo: any) => {
              stats.totalPhotos++;
              if (needsMigration(photo)) {
                stats.photosNeedingMigration++;
                photosToMigrate.push({
                  section: 'site_assessment',
                  photoType,
                  displayId: getDisplayId(photo),
                  dataUrl: getDataUrl(photo),
                  format: photo.format || 'jpeg'
                });
              } else if (photo.url && photo.url.startsWith('http')) {
                stats.photosWithCDN++;
              }
            });
          } else if (typeof photoData === 'object' && needsMigration(photoData)) {
            stats.totalPhotos++;
            stats.photosNeedingMigration++;
            photosToMigrate.push({
              section: 'site_assessment',
              photoType,
              displayId: getDisplayId(photoData),
              dataUrl: getDataUrl(photoData),
              format: photoData.format || 'jpeg'
            });
          } else if (photoData.url && photoData.url.startsWith('http')) {
            stats.photosWithCDN++;
          }
        }
      });
    }

    // 9. Scan custom field photos (image fields and boolean field photos)
    const scanCustomFieldPhotos = (sectionName: string, sectionData: any) => {
      if (!sectionData) return;
      
      Object.entries(sectionData).forEach(([fieldId, fieldValue]: [string, any]) => {
        // Check if this is a custom field with photos (starts with custom_ or ends with _photos)
        const isCustomImageField = fieldId.startsWith('custom_') && Array.isArray(fieldValue);
        const isBooleanPhotoField = fieldId.endsWith('_photos') && Array.isArray(fieldValue);
        
        if (isCustomImageField || isBooleanPhotoField) {
          (fieldValue as any[]).forEach((photoUrl: any, idx: number) => {
            // Check if it's a string (data URL) that needs migration
            if (typeof photoUrl === 'string' && photoUrl.startsWith('data:')) {
              stats.totalPhotos++;
              stats.photosNeedingMigration++;
              
              // Generate a unique display ID for this photo
              const displayId = `${fieldId}_${idx}_${Date.now()}`;
              
              photosToMigrate.push({
                section: sectionName,
                photoType: fieldId,
                displayId: displayId,
                dataUrl: photoUrl,
                format: photoUrl.includes('image/webp') ? 'webp' : 
                       photoUrl.includes('image/jpeg') ? 'jpeg' : 
                       photoUrl.includes('image/png') ? 'png' : 'jpeg'
              });
            } else if (typeof photoUrl === 'string' && photoUrl.startsWith('http')) {
              stats.totalPhotos++;
              stats.photosWithCDN++;
            }
          });
        }
      });
    };
    
    // Scan all sections for custom field photos
    const sectionsToScan = [
      'initial_questionnaire',
      'property_front',
      'property_rear',
      'property_left',
      'property_right',
      'room_details',
      'ventilation_condition',
      'site_assessment',
      'occupancy_assessment',
      'additional_notes',
      'measure_specification'
    ];
    
    sectionsToScan.forEach(sectionName => {
      const sectionData = (surveyData as any)[sectionName];
      if (sectionData) {
        scanCustomFieldPhotos(sectionName, sectionData);
      }
    });

    statsRef.current = stats;

    console.log('[CentralizedMigration] 📊 Scan complete:', {
      totalPhotos: stats.totalPhotos,
      photosNeedingMigration: stats.photosNeedingMigration,
      photosWithCDN: stats.photosWithCDN,
      photosToMigrate: photosToMigrate.length
    });

    if (photosToMigrate.length === 0) {
      console.log('[CentralizedMigration] ✅ All photos already have CDN URLs, no migration needed');
      return;
    }

    console.log(`[CentralizedMigration] 🚀 Starting migration of ${photosToMigrate.length} photos...`);

    // Queue all photos for upload
    photosToMigrate.forEach((photo, index) => {
      console.log(`[CentralizedMigration] [${index + 1}/${photosToMigrate.length}] Queueing ${photo.section}.${photo.photoType}.${photo.displayId}`);

      queuePhotoUpload(
        photo.displayId,
        photo.dataUrl,
        photo.section,
        photo.photoType,
        // On success
        async (url: string) => {
          statsRef.current.photosMigrated++;
          console.log(`[CentralizedMigration] ✅ [${statsRef.current.photosMigrated}/${photosToMigrate.length}] Migrated ${photo.section}.${photo.photoType}.${photo.displayId}`);

          // Update the specific photo in the survey data
          console.log(`[CentralizedMigration] 📝 Updating data for ${photo.section}.${photo.photoType}.${photo.displayId}`);
          onDataUpdate(prevData => {
            const updated = { ...prevData };
            const beforeSize = JSON.stringify(prevData).length;
            console.log(`[CentralizedMigration] 📊 Before update size: ${(beforeSize / 1024).toFixed(2)} KB`);

            // Handle different section structures
            if (photo.section === 'initial_questionnaire') {
              // Check if this is a custom field photo (stored directly on initial_questionnaire)
              const isCustomField = photo.photoType.startsWith('custom_') || photo.photoType.endsWith('_photos');
              
              if (isCustomField) {
                // Custom fields are stored as: initial_questionnaire.custom_XXXXX_photos = [...]
                const field = (updated.initial_questionnaire as any)[photo.photoType];
                
                if (Array.isArray(field)) {
                  console.log(`[CentralizedMigration] 🔍 Handling custom field ${photo.photoType} in initial_questionnaire (array of ${field.length} items)`);
                  
                  const newArray = field.map((photoUrl: string, idx: number) => {
                    if (typeof photoUrl === 'string' && photoUrl.startsWith('data:') && 
                        photo.dataUrl.substring(0, 100) === photoUrl.substring(0, 100)) {
                      console.log(`[CentralizedMigration] 🔄 Replacing data URL with CDN URL for ${photo.photoType}[${idx}]`);
                      console.log(`  Old: ${photoUrl.substring(0, 50)}...`);
                      console.log(`  New: ${url}`);
                      return url;
                    }
                    return photoUrl;
                  });
                  
                  updated.initial_questionnaire = {
                    ...updated.initial_questionnaire,
                    [photo.photoType]: newArray
                  };
                  
                  console.log(`[CentralizedMigration] ✓ Updated ${photo.photoType}, stillHasBase64:`, 
                    newArray.some((u: string) => typeof u === 'string' && u.startsWith('data:')));
                } else {
                  console.warn(`[CentralizedMigration] ⚠️ Custom field ${photo.photoType} is not an array:`, typeof field);
                }
              } else {
                // Built-in photos are stored as: initial_questionnaire.photos.photoType.photos = [...]
                updated.initial_questionnaire = {
                  ...updated.initial_questionnaire,
                  photos: {
                    ...updated.initial_questionnaire.photos,
                    [photo.photoType]: {
                      photos: (updated.initial_questionnaire.photos[photo.photoType]?.photos || []).map((p: any) => 
                        getDisplayId(p) === photo.displayId 
                          ? { id: p.id || photo.displayId, url, uploading: false }
                          : p
                      )
                    }
                  }
                };
              }
            } else if (photo.section === 'heating_system') {
              // Create immutable update for heating_system
              updated.heating_system = {
                ...updated.heating_system,
                photos: updated.heating_system.photos || {},
                [photo.photoType]: {
                  displayId: photo.displayId,
                  format: photo.format,
                  url,
                  uploading: false
                }
              } as any;
            } else if (['property_front', 'property_rear', 'property_left', 'property_right'].includes(photo.section)) {
              const sectionKey = photo.section as 'property_front' | 'property_rear' | 'property_left' | 'property_right';
              const section = updated[sectionKey];
              const field = (section as any)[photo.photoType];

              // Create immutable update for property sections
              if (Array.isArray(field)) {
                updated[sectionKey] = {
                  ...section,
                  [photo.photoType]: field.map((p: any) => 
                    getDisplayId(p) === photo.displayId
                      ? { displayId: photo.displayId, format: photo.format, url, uploading: false }
                      : p
                  )
                } as any;
              } else if (field && typeof field === 'object') {
                updated[sectionKey] = {
                  ...section,
                  [photo.photoType]: {
                    displayId: photo.displayId,
                    format: photo.format,
                    url,
                    uploading: false
                  }
                } as any;
              }
            } else {
              // Handle custom field photos (custom image fields and boolean field photos)
              const isCustomImageField = photo.photoType.startsWith('custom_');
              const isBooleanPhotoField = photo.photoType.endsWith('_photos');
              
              if (isCustomImageField || isBooleanPhotoField) {
                const section = (updated as any)[photo.section];
                console.log(`[CentralizedMigration] 🔍 Looking for ${photo.photoType} in ${photo.section}`, {
                  sectionExists: !!section,
                  isArray: Array.isArray(section?.[photo.photoType]),
                  arrayLength: section?.[photo.photoType]?.length
                });
                
                if (section && Array.isArray(section[photo.photoType])) {
                  const originalArray = [...section[photo.photoType]];
                  let replaced = false;
                  
                  // Create a new copy of the section to avoid mutating the original
                  const newArray = section[photo.photoType].map((photoUrl: string, idx: number) => {
                    // Match by checking if it's the same data URL (compare first 100 chars to avoid huge string comparison)
                    if (typeof photoUrl === 'string' && photoUrl.startsWith('data:') && 
                        photo.dataUrl.substring(0, 100) === photoUrl.substring(0, 100)) {
                      console.log(`[CentralizedMigration] 🔄 Replacing data URL with CDN URL for ${photo.photoType}[${idx}]`);
                      console.log(`  Old: ${photoUrl.substring(0, 50)}...`);
                      console.log(`  New: ${url}`);
                      replaced = true;
                      return url;
                    }
                    return photoUrl;
                  });
                  
                  (updated as any)[photo.section] = {
                    ...section,
                    [photo.photoType]: newArray
                  };
                  
                  if (!replaced) {
                    console.warn(`[CentralizedMigration] ⚠️ No matching data URL found for ${photo.photoType}`, {
                      searchingFor: photo.dataUrl.substring(0, 50),
                      foundUrls: originalArray.map((u: string) => typeof u === 'string' ? u.substring(0, 50) : typeof u)
                    });
                  } else {
                    // Verify the replacement worked
                    const verifyArray = (updated as any)[photo.section][photo.photoType];
                    const stillHasBase64 = verifyArray.some((u: string) => typeof u === 'string' && u.startsWith('data:'));
                    console.log(`[CentralizedMigration] ✓ Verification for ${photo.section}.${photo.photoType}:`, {
                      arrayLength: verifyArray.length,
                      stillHasBase64,
                      firstItemPreview: verifyArray[0]?.substring(0, 50)
                    });
                  }
                } else {
                  console.warn(`[CentralizedMigration] ⚠️ Could not find array for ${photo.photoType} in ${photo.section}`);
                }
              }
            }

            const afterSize = JSON.stringify(updated).length;
            const sizeDiff = beforeSize - afterSize;
            console.log(`[CentralizedMigration] 📊 After update size: ${(afterSize / 1024).toFixed(2)} KB (${sizeDiff > 0 ? '-' : '+'}${Math.abs(sizeDiff / 1024).toFixed(2)} KB)`);
            console.log(`[CentralizedMigration] 📊 Returning updated data with sectionId: "${photo.section}"`);
            
            return updated;
          }, photo.section);

          // Log progress
          if (statsRef.current.photosMigrated === photosToMigrate.length) {
            console.log('[CentralizedMigration] 🎉 All photos migrated successfully!');
          }
        },
        // On error
        (error: string) => {
          statsRef.current.errors++;
          console.error(`[CentralizedMigration] ❌ Failed to migrate ${photo.section}.${photo.photoType}.${photo.displayId}:`, error);
        }
      );
    });
  }, [surveyData, surveyId, onDataUpdate, queuePhotoUpload, isOnline]);

  // Run migration only once when survey loads
  useEffect(() => {
    // Only run migration when we have a survey ID
    if (!surveyData?.id) return;
    
    // Reset flag only when survey ID changes (not on every data update)
    const currentSurveyId = surveyData.id;
    
    if (lastSurveyIdRef.current !== currentSurveyId) {
      console.log('[CentralizedMigration] New survey detected, resetting migration flag');
      migrationRunRef.current = false;
      lastSurveyIdRef.current = currentSurveyId;
      scanAndMigratePhotos();
    } else if (!migrationRunRef.current) {
      // If same survey but migration hasn't run yet, run it
      scanAndMigratePhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData?.id]); // Only re-run when survey ID changes, not when data updates

  return {
    stats: statsRef.current,
    isComplete: statsRef.current.photosMigrated === statsRef.current.photosNeedingMigration
  };
};
